import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "marginInput",
    "extraPickerInput",
    "extraBadgeList",
    "extraBadgeTemplate",
    "taxCheckbox",
    "unitProfit",
    "unitTax",
    "unitSalePrice",
    "totalRevenue"
  ]

  static values = {
    costPerUnit: Number,
    quantity: Number,
    currencyUnit: { type: String, default: "R$" },
    extrasOptions: { type: Array, default: [] },
    extraScopePerItemLabel: { type: String, default: "Per item" },
    extraScopeShipmentLabel: { type: String, default: "Shipment total" },
    extraEstimateLabel: { type: String, default: "Estimated extra" }
  }

  connect() {
    this.recalculate()
  }

  addExtraFromPicker(event) {
    event.preventDefault()
    if (!this.hasExtraPickerInputTarget || !this.hasExtraBadgeTemplateTarget || !this.hasExtraBadgeListTarget) return

    const extraId = this.extraPickerInputTarget.value
    if (!extraId) return

    const alreadyAdded = this.extraBadgeListTarget.querySelector(`.js-extra-id[value="${extraId}"]`)
    if (alreadyAdded) {
      this.extraPickerInputTarget.selectedIndex = 0
      return
    }

    const extra = this.extrasOptionsValue.find((item) => item.id === extraId)
    if (!extra) return

    const wrapper = document.createElement("div")
    wrapper.innerHTML = this.extraBadgeTemplateTarget.innerHTML.trim()
    const badge = wrapper.firstElementChild
    if (!badge) return

    badge.dataset.extraId = extra.id
    badge.dataset.costModel = extra.cost_model
    badge.dataset.fixedCents = extra.fixed_cents
    badge.dataset.valuePercent = extra.value_percent
    badge.querySelector(".js-extra-id").value = extra.id
    badge.querySelector(".js-extra-name").textContent = extra.name

    this.extraBadgeListTarget.appendChild(badge)
    this.extraPickerInputTarget.selectedIndex = 0
    this.recalculate()
  }

  removeExtra(event) {
    event.preventDefault()
    const badge = event.target.closest("[data-extra-badge]")
    if (!badge) return

    badge.remove()
    this.recalculate()
  }

  scopeChanged() {
    this.recalculate()
  }

  recalculate() {
    const costPerUnit = this.costPerUnitValue
    const quantity = this.quantityValue
    const marginPercent = parseFloat(this.marginInputTarget.value) || 0
    const commercialExtraCents = this.calculateCommercialExtraCents(costPerUnit, quantity)

    let taxPercent = 0
    this.taxCheckboxTargets.forEach((cb) => {
      if (cb.checked) {
        taxPercent += parseFloat(cb.dataset.taxPercent) || 0
      }
    })

    const commercialExtraUnitCents = quantity > 0 ? Math.round(commercialExtraCents / quantity) : 0
    const baseUnitCents = costPerUnit + commercialExtraUnitCents
    const unitProfitCents = Math.round(baseUnitCents * (marginPercent / 100))
    const taxableAmountCents = baseUnitCents + unitProfitCents
    const unitTaxCents = Math.round(taxableAmountCents * taxPercent)
    const unitSalePriceCents = taxableAmountCents + unitTaxCents
    const totalRevenueCents = Math.round(unitSalePriceCents * quantity)

    if (this.hasUnitProfitTarget) {
      this.unitProfitTarget.textContent = this.formatCents(unitProfitCents)
    }
    if (this.hasUnitTaxTarget) {
      this.unitTaxTarget.textContent = this.formatCents(unitTaxCents)
    }
    if (this.hasUnitSalePriceTarget) {
      this.unitSalePriceTarget.textContent = this.formatCents(unitSalePriceCents)
    }
    if (this.hasTotalRevenueTarget) {
      this.totalRevenueTarget.textContent = this.formatCents(totalRevenueCents)
    }
  }

  calculateCommercialExtraCents(costPerUnit, quantity) {
    if (!this.hasExtraBadgeListTarget) return 0

    const badges = this.extraBadgeListTarget.querySelectorAll("[data-extra-badge]")
    let total = 0

    badges.forEach((badge) => {
      const costModel = badge.dataset.costModel || "fixed"
      const fixedCents = parseInt(badge.dataset.fixedCents || "0", 10)
      const valuePercent = parseFloat(badge.dataset.valuePercent || "0")
      const scope = badge.querySelector(".js-extra-scope")?.value
      const estimateNode = badge.querySelector(".js-extra-estimate")
      const extraTotalCents = this.extraTotalForScope({
        costModel,
        fixedCents,
        valuePercent,
        scope,
        costPerUnit,
        quantity
      })

      if (estimateNode) {
        estimateNode.textContent = `${this.extraEstimateLabelValue}: ${this.formatCents(extraTotalCents)}`
      }
      total += extraTotalCents
    })

    return total
  }

  extraTotalForScope({ costModel, fixedCents, valuePercent, scope, costPerUnit, quantity }) {
    const unitBaseCost = Math.round(costPerUnit)
    const totalBaseCost = Math.round(costPerUnit * quantity)

    if (scope === "per_item") {
      const perItem = this.extraCostForModel({ costModel, fixedCents, valuePercent, baseCost: unitBaseCost })
      return Math.round(perItem * quantity)
    }

    if (scope === "shipment") {
      return this.extraCostForModel({ costModel, fixedCents, valuePercent, baseCost: totalBaseCost })
    }

    return 0
  }

  extraCostForModel({ costModel, fixedCents, valuePercent, baseCost }) {
    if (costModel === "fixed") return fixedCents
    if (costModel === "percent_cost") return Math.round(baseCost * valuePercent)
    if (costModel === "percent_sale") return 0
    return fixedCents
  }

  formatCents(cents) {
    const value = (cents / 100).toFixed(2)
    const parts = value.split(".")
    const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    return `${this.currencyUnitValue} ${intPart},${parts[1]}`
  }
}
